import {type AtpSessionData} from '@atproto/api'

import {
  deleteAccount,
  getCapabilities,
  requestDeleteAccount,
} from '#/lib/wsoc/account'
import {
  type DeleteAccountParams,
  type DeleteAccountResponse,
  type GetCapabilitiesResponse,
  type RequestDeleteAccountResponse,
  type RequestDeleteParams,
} from '#/lib/wsoc/account/types'
import {
  getVerificationDetails,
  type VerificationDetails,
} from '#/lib/wsoc/actor'
import {
  getContentSourceFromServer,
  setContentSourceOnServer,
} from '#/lib/wsoc/contentSource/api'
import {
  type ContentSource,
  type GetContentSourceRes,
} from '#/lib/wsoc/contentSource/types'
import {
  allocateWidForAccount,
  getLinkedAccounts,
  getReauthToken,
  getVerificationStatus,
  init,
  linkWid,
  silentReauth,
  status,
} from '#/lib/wsoc/quicklogin'
import {type GetReauthTokenResponse} from '#/lib/wsoc/quicklogin/getReauthToken'
import {type LinkWidResponse} from '#/lib/wsoc/quicklogin/linkWid'
import {type SilentReauthResponse} from '#/lib/wsoc/quicklogin/silentReauth'
import {
  type AllocateWidResponse,
  type GetLinkedAccountsResponse,
  type GetVerificationStatusResponse,
  type QuickLoginInitParams,
  type QuickLoginInitResponse,
  type QuickLoginStatusParams,
  type QuickLoginStatusResponse,
} from '#/lib/wsoc/quicklogin/types'
import {type AccountType} from '#/wsocial/types/profile.ts'
import {BskyAppAgent} from './bskyAppAgent'

export type WsocialSession = AtpSessionData & {
  wsocialVerified?: string | null
  wsocialAccountType?: AccountType
}

export class WsocAgent extends BskyAppAgent {
  quicklogin = {
    init: (
      params: QuickLoginInitParams,
      options?: {signal?: AbortSignal},
    ): Promise<QuickLoginInitResponse> => init(this, params, options),
    status: (
      params: QuickLoginStatusParams,
      options?: {signal?: AbortSignal},
    ): Promise<QuickLoginStatusResponse> => status(this, params, options),
    getLinkedAccounts: (options?: {
      signal?: AbortSignal
    }): Promise<GetLinkedAccountsResponse> => getLinkedAccounts(this, options),
    linkWid: (options?: {
      signal?: AbortSignal
      serviceId?: string
      sessionId?: string
    }): Promise<LinkWidResponse> => linkWid(this, options),
    allocateWidForAccount: (options?: {
      signal?: AbortSignal
    }): Promise<AllocateWidResponse> => allocateWidForAccount(this, options),
    silentReauth: (
      reauthJwt: string,
      options?: {signal?: AbortSignal},
    ): Promise<SilentReauthResponse> => silentReauth(this, reauthJwt, options),
    getReauthToken: (options?: {
      signal?: AbortSignal
    }): Promise<GetReauthTokenResponse> => getReauthToken(this, options),
    getVerificationStatus: (options?: {
      signal?: AbortSignal
    }): Promise<GetVerificationStatusResponse> =>
      getVerificationStatus(this, options),
  }

  account = {
    requestDeleteAccount: (
      params: RequestDeleteParams = {},
      options?: {signal?: AbortSignal},
    ): Promise<RequestDeleteAccountResponse> =>
      requestDeleteAccount(this, params, options),
    deleteAccount: (
      params: DeleteAccountParams,
      options?: {signal?: AbortSignal},
    ): Promise<DeleteAccountResponse> => deleteAccount(this, params, options),
    getCapabilities: (options?: {
      signal?: AbortSignal
    }): Promise<GetCapabilitiesResponse> => getCapabilities(this, options),
    getVerificationDetails: (
      did: string,
      options?: {signal?: AbortSignal},
    ): Promise<VerificationDetails | null> =>
      getVerificationDetails(this, did, options),
  }

  contentSource = {
    get: (options?: {signal?: AbortSignal}): Promise<GetContentSourceRes> => {
      const accessJwt = this.session?.accessJwt
      if (!accessJwt)
        throw new Error('contentSource.get requires an authenticated session')
      return getContentSourceFromServer(accessJwt, options)
    },
    set: (
      source: ContentSource,
      options?: {signal?: AbortSignal},
    ): Promise<void> => {
      const accessJwt = this.session?.accessJwt
      if (!accessJwt)
        throw new Error('contentSource.set requires an authenticated session')
      return setContentSourceOnServer(accessJwt, source, options)
    },
  }
}
